import { ActionIcon } from '@mantine/core';
import { Html, Line } from '@react-three/drei';
import { Icon, MantineOverrideProvider, type SymbolCodepoints } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import {
    PANEL_REPLACEMENT_LABEL_Z_INDEX_RANGE,
    PANEL_REPLACEMENT_OVERLAY_Z_INDEX_RANGE,
    getControlPosition,
    getEdgeKey,
    getNextPanel,
    getPanelDimension,
    getPartAxisScale,
    getSnapGridAxes,
    getSnapMarkPoints,
    isRotation90Degrees
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type {
    PanelReplacementDirection,
    QuickPanelReplacementControlsProps
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';

const labelsByDirection = {
    left: 'labelInputActionPanelQuickReplacementLeft',
    right: 'labelInputActionPanelQuickReplacementRight',
    up: 'labelInputActionPanelQuickReplacementUp',
    down: 'labelInputActionPanelQuickReplacementDown'
} as const satisfies Record<PanelReplacementDirection, string>;

const arrowsByDirection: Record<PanelReplacementDirection, SymbolCodepoints> = {
    left: 'arrow_left',
    right: 'arrow_right',
    up: 'arrow_drop_up',
    down: 'arrow_drop_down'
};

export const QuickPanelReplacementControls = ({
    camera,
    grid,
    selected,
    anchorPart,
    edgeHandles,
    activeEdge,
    screenDirections,
    mousePreview,
    snapCandidates,
    controlsEnabled,
    selectionToken,
    onEdgeClick
}: QuickPanelReplacementControlsProps) => {
    const { t } = useTranslation();
    const rotated90 = isRotation90Degrees(anchorPart.rotation);
    const compatiblePartCount = Object.keys(selected.instance.parts).length;
    const activeEdgeDirection = activeEdge ? screenDirections[getEdgeKey(activeEdge)] : undefined;
    const snapLabelDirection = mousePreview ? screenDirections[getEdgeKey(mousePreview.edge)] : undefined;

    const snapGridAxes =
        activeEdge && activeEdgeDirection ? getSnapGridAxes(activeEdge, activeEdgeDirection, camera) : undefined;

    const snapLabelAxes =
        mousePreview && snapLabelDirection ? getSnapGridAxes(mousePreview.edge, snapLabelDirection, camera) : undefined;

    const snapLabelPoints =
        mousePreview && snapLabelAxes
            ? getSnapMarkPoints(
                  mousePreview.target,
                  selected.item,
                  mousePreview.edge,
                  rotated90,
                  getPartAxisScale(anchorPart.scale, mousePreview.edge.localAxis),
                  snapLabelAxes.origin,
                  snapLabelAxes.gridAxis,
                  snapLabelAxes.markerAxis
              )
            : null;

    const snapLabelCenter = snapLabelPoints
        ? snapLabelPoints[0].clone().add(snapLabelPoints[1]).multiplyScalar(0.5)
        : null;

    return (
        <>
            {edgeHandles.map((edge) => {
                const edgeKey = getEdgeKey(edge);
                const direction = screenDirections[edgeKey];
                const isActive = activeEdge !== null && getEdgeKey(activeEdge) === edgeKey;
                const hasReplacement = ([-1, 1] as const).some(
                    (resizeDirection) =>
                        getNextPanel(
                            grid,
                            selected.item,
                            edge.dimension,
                            resizeDirection,
                            rotated90,
                            compatiblePartCount
                        ) !== undefined
                );

                return (
                    <Html
                        key={edgeKey}
                        calculatePosition={(_, currentCamera, size) => {
                            const point = getControlPosition(edge, direction, currentCamera).project(currentCamera);
                            return [((point.x + 1) * size.width) / 2, ((1 - point.y) * size.height) / 2];
                        }}
                        distanceFactor={0.0019}
                        occlude={false}
                        position={edge.anchor}
                        zIndexRange={PANEL_REPLACEMENT_OVERLAY_Z_INDEX_RANGE}
                        center
                    >
                        <MantineOverrideProvider>
                            <ActionIcon
                                aria-label={t(labelsByDirection[direction])}
                                aria-pressed={isActive}
                                className="pointer-events-auto"
                                disabled={!controlsEnabled || !hasReplacement}
                                style={{
                                    backgroundColor: isActive
                                        ? 'var(--color-background-accent)'
                                        : 'var(--color-background-default)',
                                    border: '2px solid var(--color-border-accent)',
                                    color: isActive ? 'var(--color-text-inverted)' : 'var(--color-text-default)'
                                }}
                                title={t(labelsByDirection[direction])}
                                variant={isActive ? 'filled' : 'light'}
                                onClick={() => onEdgeClick(edge)}
                                onPointerDown={(event) => event.stopPropagation()}
                            >
                                <Icon icon={arrowsByDirection[direction]} size="5xl" />
                            </ActionIcon>
                        </MantineOverrideProvider>
                    </Html>
                );
            })}
            {activeEdge &&
                snapGridAxes &&
                snapCandidates.map((candidate) => {
                    const [start, end] = getSnapMarkPoints(
                        candidate,
                        selected.item,
                        activeEdge,
                        rotated90,
                        getPartAxisScale(anchorPart.scale, activeEdge.localAxis),
                        snapGridAxes.origin,
                        snapGridAxes.gridAxis,
                        snapGridAxes.markerAxis
                    );
                    return (
                        <Line
                            key={candidate.article.id}
                            color={PRIMARY_COLOR_ACCENT_HEX}
                            depthTest={false}
                            depthWrite={false}
                            lineWidth={5}
                            name="panel-replacement-snap-marker"
                            points={[start, end]}
                            renderOrder={1000}
                            transparent
                        />
                    );
                })}
            {mousePreview &&
                snapLabelPoints &&
                snapLabelAxes &&
                snapLabelCenter &&
                mousePreview.selectionToken === selectionToken && (
                    <>
                        <Line
                            color={PRIMARY_COLOR_ACCENT_HEX}
                            dashSize={0.04}
                            depthTest={false}
                            depthWrite={false}
                            gapSize={0.04}
                            lineWidth={5}
                            name="panel-replacement-snap-guide"
                            points={[snapLabelAxes.origin, snapLabelCenter]}
                            renderOrder={1000}
                            dashed
                            transparent
                        />
                        <Html
                            calculatePosition={(_, currentCamera, size) => {
                                const [start, end] = snapLabelPoints.map((point) =>
                                    point.clone().project(currentCamera)
                                );
                                const endpoint =
                                    snapLabelDirection === 'up' || snapLabelDirection === 'down'
                                        ? start.x < end.x
                                            ? start
                                            : end
                                        : start.y > end.y
                                          ? start
                                          : end;
                                return [((endpoint.x + 1) * size.width) / 2, ((1 - endpoint.y) * size.height) / 2];
                            }}
                            occlude={false}
                            pointerEvents="none"
                            position={snapLabelCenter}
                            style={{ pointerEvents: 'none' }}
                            zIndexRange={PANEL_REPLACEMENT_LABEL_Z_INDEX_RANGE}
                        >
                            <span
                                className="pointer-events-none absolute block whitespace-nowrap rounded bg-background-gray-ultra-light px-2 py-1 text-xs shadow"
                                style={
                                    snapLabelDirection === 'up' || snapLabelDirection === 'down'
                                        ? { transform: 'translate(calc(-100% - 12px), -50%)' }
                                        : {
                                              transform:
                                                  'translate(-50%, -50%) rotate(-90deg) translateX(calc(50% + 12px))'
                                          }
                                }
                            >
                                {mousePreview.target.article.articleNumber}
                                {' · '}
                                {getPanelDimension(mousePreview.target, 'width', rotated90).toFixed(2)}
                                {' x '}
                                {getPanelDimension(mousePreview.target, 'height', rotated90).toFixed(2)}
                                {' m'}
                            </span>
                        </Html>
                    </>
                )}
        </>
    );
};
